import { useState, useEffect } from "react";
import { parseHiddenList, serializeHiddenList } from "@/lib/email/hiddenMailboxes";
import { parseAccountOverviewView, type AccountOverviewView } from "@/lib/accountOverview";

function getStoredBool(key: string, defaultValue: boolean): boolean {
  try {
    const v = localStorage.getItem(key);
    if (v === null) return defaultValue;
    return JSON.parse(v) as boolean;
  } catch {
    return defaultValue;
  }
}

export function useUserPref(key: string, defaultValue: boolean): [boolean, (v: boolean) => void] {
  const [value, setValue] = useState(() => getStoredBool(key, defaultValue));
  const set = (v: boolean) => {
    setValue(v);
    try { localStorage.setItem(key, JSON.stringify(v)); } catch {}
  };
  return [value, set];
}

// Preference keys
export const PREF_SIDEBAR_AUTO_COLLAPSE = "centcom_pref_sidebar_auto_collapse";
export const PREF_HELP_AUTO_DISMISS = "centcom_pref_help_auto_dismiss";
export const PREF_TIME_FORMAT = "centcom_pref_time_format";
export const PREF_ACCOUNT_RIGHT_COLLAPSED = "centcom_pref_account_right_collapsed";
export const PREF_ACCOUNT_OVERVIEW_VIEW = "centcom_pref_account_overview_view";
/** Portal Responses -> Documents tab sort: true = oldest first (triage order), false = newest first. */
export const PREF_PORTAL_RESPONSES_OLDEST_FIRST = "centcom_pref_portal_responses_oldest_first";
export const PREF_HIDDEN_MAILBOXES = "centcom_pref_hidden_mailboxes";

/** Per-user set of mailbox addresses hidden from /inbox + the pop-out. localStorage, per-browser. */
export function useHiddenMailboxes(): [Set<string>, (mailbox: string, hidden: boolean) => void] {
  const [value, setValue] = useState<Set<string>>(() => {
    try {
      return parseHiddenList(localStorage.getItem(PREF_HIDDEN_MAILBOXES));
    } catch {
      return new Set();
    }
  });
  const setHidden = (mailbox: string, hidden: boolean) => {
    setValue((prev) => {
      const next = new Set(prev);
      const key = mailbox.toLowerCase();
      if (hidden) next.add(key);
      else next.delete(key);
      try { localStorage.setItem(PREF_HIDDEN_MAILBOXES, serializeHiddenList(next)); } catch {}
      return next;
    });
  };

  // Keep multiple windows (the /inbox tab and its pop-out) in sync when one of them
  // hides/unhides a mailbox — localStorage writes in one window don't update React state
  // in another without listening for the "storage" event.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== PREF_HIDDEN_MAILBOXES) return;
      try {
        setValue(parseHiddenList(e.newValue));
      } catch {
        setValue(new Set());
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  return [value, setHidden];
}

export type TimeFormat = "decimal" | "hm";

export function useTimeFormat(): [TimeFormat, (v: TimeFormat) => void] {
  const [value, setValue] = useState<TimeFormat>(() => {
    try {
      const v = localStorage.getItem(PREF_TIME_FORMAT);
      return v === "hm" ? "hm" : "decimal";
    } catch {
      return "decimal";
    }
  });
  const set = (v: TimeFormat) => {
    setValue(v);
    try { localStorage.setItem(PREF_TIME_FORMAT, v); } catch {}
  };
  return [value, set];
}

export type { AccountOverviewView };

/**
 * Per-browser Classic | Grid layout for the account page Overview tab (v1.5.75).
 * Default classic: nobody's page changes on deploy; the choice sticks once flipped.
 */
export function useAccountOverviewView(): [AccountOverviewView, (v: AccountOverviewView) => void] {
  const [value, setValue] = useState<AccountOverviewView>(() => {
    try {
      return parseAccountOverviewView(localStorage.getItem(PREF_ACCOUNT_OVERVIEW_VIEW));
    } catch {
      return "classic";
    }
  });
  const set = (v: AccountOverviewView) => {
    setValue(v);
    try { localStorage.setItem(PREF_ACCOUNT_OVERVIEW_VIEW, v); } catch {}
  };
  return [value, set];
}

/** Format a decimal hours value for display. decimals only used in 'decimal' mode. */
export function formatHours(hours: number, fmt: TimeFormat = "decimal", decimals = 2): string {
  if (fmt === "hm") {
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    if (h === 0) return `${m}m`;
    if (m === 0) return `${h}h`;
    return `${h}h ${m}m`;
  }
  return `${hours.toFixed(decimals)}h`;
}
